import { PHASE, PLAYER } from "../../common/constants.ts";
import {
	CHECKPOINT_CYCLE,
	CHECKPOINT_LABELS,
	MP_FUTURE_DRAFT_PICK_SEASONS,
	type MpCheckpoint,
} from "../../common/multiplayerConfig.ts";
import type {
	Conditions,
	MpLiveGame,
	MpLiveScores,
} from "../../common/types.ts";
import {
	draft,
	expansionDraft as expansionDraftCore,
	freeAgents,
	game,
	league,
	phase,
	player,
	season,
} from "../core/index.ts";
import { idb } from "../db/index.ts";
import {
	g,
	helpers,
	local,
	lock,
	logEvent,
	toUI,
	updateStatus,
} from "../util/index.ts";
import { kvGet, kvRemove, kvSet } from "./authStorage.ts";
import { exportSnapshotBlob } from "./exportSnapshot.ts";
import {
	getClient,
	getLeagueIdForLid,
	getLobbyState,
	getSession,
	simDirtyKey,
} from "./index.ts";
import { broadcastToChannel, markSeqApplied, syncNow } from "./sync.ts";
import { collectAssets } from "./trades.ts";
import { applyPendingFirings, stageFirings } from "./firing.ts";

// ============================================================================
// Phase 4: the sim runner. When everyone readies up (sim_open), the runner's
// Play menu unlocks and they simulate like normal BBGM - day, week, month -
// with a hard stop at the next checkpoint. Then the finished segment is
// published as a canonical snapshot everyone else imports. Only the runner
// ever rolls dice.
// ============================================================================

export const nextCheckpoint = (checkpoint: MpCheckpoint): MpCheckpoint => {
	const i = CHECKPOINT_CYCLE.indexOf(checkpoint);
	return CHECKPOINT_CYCLE[(i + 1) % CHECKPOINT_CYCLE.length]!;
};

const dirtyKey = simDirtyKey;

export const isSimDirty = async (leagueId: string) =>
	(await kvGet(dirtyKey(leagueId))) !== null;

// ============================================================================
// Boundary predicates (local state only - checked after every simmed day)
// ============================================================================

export const boundaryReached = async (
	target: MpCheckpoint,
): Promise<boolean> => {
	const currentPhase = g.get("phase");

	switch (target) {
		case "game25": {
			if (currentPhase < PHASE.REGULAR_SEASON) {
				return false;
			}
			if (currentPhase > PHASE.REGULAR_SEASON) {
				return true; // overshoot safety
			}
			const currentSeason = g.get("season");
			const teamSeasons = await idb.cache.teamSeasons.getAll();
			const gps = teamSeasons
				.filter((row) => row.season === currentSeason)
				.map((row) => row.won + row.lost + (row.tied ?? 0) + (row.otl ?? 0));
			return gps.length > 0 && Math.min(...gps) >= 25;
		}

		case "deadline": {
			if (currentPhase >= PHASE.AFTER_TRADE_DEADLINE) {
				return true; // deadline passed - overshoot safety
			}
			if (currentPhase < PHASE.REGULAR_SEASON) {
				return false;
			}
			const schedule = await season.getSchedule();
			const marker = schedule.findIndex(
				(gm) => gm.homeTid === -3 && gm.awayTid === -3,
			);
			if (marker === -1) {
				// League has no trade deadline scheduled; treat as reached so the
				// cycle can continue
				return true;
			}
			return marker === 0;
		}

		case "playoffs":
			// Stop the moment the playoffs begin (bracket is set, no games played)
			return (
				currentPhase >= PHASE.PLAYOFFS && currentPhase < PHASE.DRAFT_LOTTERY
			);

		case "lottery":
			// Stop the moment the draft lottery is drawn (pick order is set), before
			// the draft itself, so teams can trade for a known lottery pick
			return currentPhase >= PHASE.DRAFT_LOTTERY && currentPhase < PHASE.DRAFT;

		case "draft":
			// Stop at the draft, before picks are made
			return currentPhase >= PHASE.DRAFT && currentPhase < PHASE.RESIGN_PLAYERS;

		case "resign":
			// Stop the moment re-signing begins, so humans re-sign their own
			// expiring players before the auction
			return (
				currentPhase >= PHASE.RESIGN_PLAYERS && currentPhase < PHASE.FREE_AGENCY
			);

		case "fa":
			// Stop the moment free agency begins, before any FA days sim - the
			// blind-bid auction happens here
			return currentPhase === PHASE.FREE_AGENCY;

		case "offseason":
			return currentPhase === PHASE.PRESEASON;
	}
};

// ============================================================================
// Phase 6: shared playoffs
// ============================================================================

// Human teams in the playoffs (main bracket's current round, plus any play-in
// teams - a play-in spot is still a playoff berth to fight for). Used for the
// ready-gate at the playoffs checkpoint.
export const computeAliveHumanTids = async (): Promise<number[]> => {
	const ps = await idb.cache.playoffSeries.get(g.get("season"));
	if (!ps) {
		return [];
	}
	const userTids = g.get("userTids");
	const rnd = ps.currentRound;
	if (rnd < 0 || !ps.series[rnd]) {
		return [];
	}
	const numGamesToWin = helpers.numGamesToWinSeries(
		g.get("numGamesPlayoffSeries", "current")[rnd],
	);
	const alive = new Set<number>();

	// Play-in tournament teams (the bracket's 7/8 seeds are placeholders until
	// the play-in resolves, so these humans aren't in series[0] yet)
	if (ps.playIns) {
		for (const playIn of ps.playIns) {
			for (const matchup of playIn) {
				if (userTids.includes(matchup.home.tid)) {
					alive.add(matchup.home.tid);
				}
				if (matchup.away !== undefined && userTids.includes(matchup.away.tid)) {
					alive.add(matchup.away.tid);
				}
			}
		}
	}

	for (const { home, away } of ps.series[rnd]!) {
		if (!away) {
			if (userTids.includes(home.tid)) {
				alive.add(home.tid);
			}
			continue;
		}
		const decided = home.won >= numGamesToWin || away.won >= numGamesToWin;
		if (!decided) {
			if (userTids.includes(home.tid)) {
				alive.add(home.tid);
			}
			if (userTids.includes(away.tid)) {
				alive.add(away.tid);
			}
		} else {
			const winner = home.won >= numGamesToWin ? home.tid : away.tid;
			if (userTids.includes(winner)) {
				alive.add(winner);
			}
		}
	}
	return [...alive];
};

// ============================================================================
// Phase 6b: live multiplayer draft
// ============================================================================

// Best remaining draftable player, deterministic (value desc, pid asc).
// Handles both the annual draft (current class only) and the fantasy draft
// (every pooled player is UNDRAFTED).
const bestAvailablePid = async (): Promise<number | undefined> => {
	let pool;
	if (g.get("phase") === PHASE.FANTASY_DRAFT) {
		pool = await idb.cache.players.indexGetAll(
			"playersByTid",
			PLAYER.UNDRAFTED,
		);
	} else {
		pool = (
			await idb.cache.players.indexGetAll("playersByDraftYearRetiredYear", [
				[g.get("season")],
				[g.get("season"), Infinity],
			])
		).filter((p) => p.tid === PLAYER.UNDRAFTED);
	}
	if (pool.length === 0) {
		return undefined;
	}
	pool.sort((x, y) => y.value - x.value || x.pid - y.pid);
	return pool[0]!.pid;
};

// Auto-pick AI teams until a human is on the clock (or the draft ends), then
// publish a draft slice so everyone sees the picks and the human on the clock
// can pick. Humans with auto_draft set ("sim the rest of my picks") get
// best-available picks made for them by the runner instead of pausing.
// Returns "paused" (human on the clock) or "ended" (draft complete).
// v17: selections made since the last publish, so the annual draft can be
// broadcast as a small event instead of a full league snapshot. Recorded by
// whoever calls selectPlayer on the runner (AI auto-picks here, the human's
// pick in processDraftPick).
export type DraftSelection = {
	dpid: number;
	tid: number;
	round: number;
	pick: number;
	originalTid: number;
	pid: number;
};
let pendingSelections: DraftSelection[] = [];

export const recordDraftSelection = (dp: DraftSelection) => {
	pendingSelections.push(dp);
};

// Event-based publishing only covers the ANNUAL draft. The fantasy draft keeps
// using snapshots: selectPlayer assigns a random jersey number there when it
// runs during an ongoing season, so it isn't deterministic across clients.
const draftUsesEvents = () => g.get("phase") === PHASE.DRAFT;

export const simDraftSlice = async (
	conditions: Conditions,
	leagueId: string,
	leagueName: string,
): Promise<"paused" | "ended"> => {
	// runPicks auto-picks AI teams and stops the moment a human team (any tid in
	// userTids) is on the clock. Spectator must be OFF so it pauses for humans.
	if (g.get("spectator")) {
		await league.setGameAttributes({ spectator: false });
	}

	const { members } = await getLobbyState(leagueId);
	const autoTids = new Set(
		members.filter((m) => m.auto_draft && m.tid !== null).map((m) => m.tid),
	);

	// When the final pick is made, runPicks itself completes the draft and
	// ADVANCES THE PHASE (fantasy -> preseason restore, annual -> after
	// draft). Detect completion by that phase change, NOT by getOrder():
	// once the fantasy picks are consumed, getOrder() returns the NEXT
	// ANNUAL draft's picks, and treating those as "still drafting" would
	// burn real future picks and never finish. (This exact bug shipped once.)
	const draftPhase = g.get("phase");

	// Bounded well above any draft's length (fantasy = rounds x teams)
	for (let i = 0; i < 2000; i++) {
		// runPicks consumes the order front-to-back and returns the pids it
		// drafted in that same sequence (see runPicks.ts - it shifts draftPicks),
		// so pairing them by index recovers exactly which team took whom. That's
		// what gets broadcast instead of a snapshot.
		const orderBefore = await draft.getOrder();
		const autoPickedPids = await draft.runPicks(
			{ type: "untilYourNextPick" },
			conditions,
		);
		for (const [j, pid] of (autoPickedPids ?? []).entries()) {
			const dp = orderBefore[j];
			if (dp) {
				recordDraftSelection({
					dpid: dp.dpid,
					tid: dp.tid,
					round: dp.round,
					pick: dp.pick,
					originalTid: dp.originalTid,
					pid,
				});
			}
		}

		if (g.get("phase") !== draftPhase) {
			// Draft finished (runPicks ran its completion internally)
			return "ended";
		}

		const order = await draft.getOrder();
		if (order.length === 0) {
			// Draft finished
			return "ended";
		}

		const onClock = order[0]!.tid;
		if (autoTids.has(onClock)) {
			// This human asked to sim their picks: best available, deterministic
			const pid = await bestAvailablePid();
			if (pid !== undefined) {
				recordDraftSelection({ ...order[0]!, pid });
				await draft.selectPlayer(order[0]!, pid);
				await draft.afterPicks(false, conditions);
				continue;
			}
		}

		await publishDraftSlice(leagueId, leagueName, onClock);
		return "paused";
	}
	throw new Error("Draft did not terminate");
};

let publishingDraft = false;
let publishingDraftStarted = 0;
const publishDraftSlice = async (
	leagueId: string,
	leagueName: string,
	onClock: number,
) => {
	// If a previous publish hangs (a snapshot upload that never settles), this
	// flag would otherwise stay true for the rest of the session and silently
	// mute EVERY later slice - the draft keeps advancing on the runner's screen
	// while nobody else ever receives a board. Time it out so a wedged attempt
	// can't permanently stop publishing.
	if (publishingDraft && Date.now() - publishingDraftStarted < 60 * 1000) {
		return;
	}
	publishingDraft = true;
	publishingDraftStarted = Date.now();
	try {
		// v17 fast path: broadcast just the picks. Everyone applies them with the
		// same deterministic selectPlayer instead of rebuilding their whole
		// league from a snapshot. Annual draft only - see draftUsesEvents.
		if (draftUsesEvents()) {
			await idb.cache.flush();
			const selections = pendingSelections;
			pendingSelections = [];
			const { data, error } = await getClient().rpc("publish_draft_picks", {
				p_league_id: leagueId,
				p_selections: selections,
				p_on_clock: onClock,
			});
			if (error) {
				// The RPC is missing (schema not updated yet) or failed. Fall
				// through to publishing a full snapshot rather than stalling the
				// draft - slow, but exactly what every earlier version did.
				console.error(
					"publish_draft_picks failed, falling back to a snapshot:",
					error.message,
				);
			} else {
				await markSeqApplied(leagueId, Number(data.seq));

				const t = g.get("teamInfoCache")[onClock];
				logEvent({
					type: "info",
					text: `On the clock: <b>${t ? `${t.region} ${t.name}` : `Team ${onClock}`}</b>. Their GM picks on the Draft page.`,
					saveToDb: false,
					showNotification: true,
				});
				await toUI("realtimeUpdate", [["playerMovement", "multiplayer"]]);
				return;
			}
		}

		await idb.cache.flush();
		const blob = await exportSnapshotBlob(leagueName);
		const path = `${leagueId}/draft-${Date.now()}.json.gz`;
		const supabase = getClient();
		const upload = await supabase.storage
			.from("snapshots")
			.upload(path, blob, { upsert: true, contentType: "application/gzip" });
		if (upload.error) {
			throw new Error(`Draft snapshot upload failed: ${upload.error.message}`);
		}
		const { data, error } = await supabase.rpc("publish_draft_slice", {
			p_league_id: leagueId,
			p_snapshot_path: path,
			p_on_clock: onClock,
		});
		if (error) {
			throw new Error(error.message);
		}
		await markSeqApplied(leagueId, Number(data.seq));

		const t = g.get("teamInfoCache")[onClock];
		logEvent({
			type: "info",
			text: `On the clock: <b>${t ? `${t.region} ${t.name}` : `Team ${onClock}`}</b>. Their GM picks on the Draft page.`,
			saveToDb: false,
			showNotification: true,
		});
		void cleanupOldSnapshots(leagueId, path);
		await toUI("realtimeUpdate", [["playerMovement", "multiplayer"]]);
	} finally {
		publishingDraft = false;
		await updateStatus("Idle");
	}
};

// v16: a scheduled expansion draft (e.g. Dallas 1980) fired mid-segment. Pause
// here: publish the current state (phase EXPANSION_DRAFT, protection) WITHOUT
// advancing the checkpoint, so every human imports it and protects their own
// players on the Protect Players page ('protect' events). When everyone
// readies up again, the runner's next sim resolves the expansion draft (see
// the EXPANSION_DRAFT branch of the sim loop) and continues the segment.
const publishExpansionPause = async (leagueId: string, leagueName: string) => {
	await idb.cache.flush();
	const blob = await exportSnapshotBlob(leagueName);
	const path = `${leagueId}/expansion-${Date.now()}.json.gz`;
	const supabase = getClient();
	const upload = await supabase.storage
		.from("snapshots")
		.upload(path, blob, { upsert: true, contentType: "application/gzip" });
	if (upload.error) {
		throw new Error(
			`Expansion snapshot upload failed: ${upload.error.message}`,
		);
	}
	const { data, error } = await supabase.rpc("publish_expansion_pause", {
		p_league_id: leagueId,
		p_snapshot_path: path,
	});
	if (error) {
		throw new Error(error.message);
	}
	await markSeqApplied(leagueId, Number(data.seq));
	logEvent({
		type: "info",
		text: "<b>Expansion draft!</b> New teams are joining the league. Protect your players on the Protect Players page, then ready up to continue.",
		saveToDb: false,
		showNotification: true,
	});
	void cleanupOldSnapshots(leagueId, path);
	await toUI("realtimeUpdate", [["playerMovement", "multiplayer"]]);
};

// The runner processes a human's submitted pick: apply it, then auto-pick AI up
// to the next human (or finish the draft). Called from the realtime handler
// when draft_pending_pid appears, or directly when the runner is on the clock.
let processingDraftPick = false;
let processingDraftPickStarted = 0;

// A wedged in-flight pick (an await that never settles) must not disable pick
// processing and the reconcile poll for the rest of the session.
const draftPickProcessingBusy = () =>
	processingDraftPick && Date.now() - processingDraftPickStarted < 60 * 1000;
export const processDraftPick = async (
	leagueId: string,
	conditions: Conditions,
	retriesLeft = 40,
): Promise<void> => {
	// Keep the safety-net poll running while the draft is live (idempotent)
	ensureDraftReconcile(leagueId);
	// Another pick/slice is being processed. Don't drop this one: a human's
	// submitted pick (draft_pending_pid) arriving during a busy window would be
	// lost with no retrigger, wedging the draft. Retry once the lock frees.
	if (draftPickProcessingBusy()) {
		if (retriesLeft > 0) {
			setTimeout(() => {
				void processDraftPick(leagueId, conditions, retriesLeft - 1);
			}, 250);
		}
		return;
	}
	processingDraftPick = true;
	processingDraftPickStarted = Date.now();
	try {
		const { league: mpLeague } = await getLobbyState(leagueId);
		const session = await getSession();
		if (
			!mpLeague.draft_active ||
			mpLeague.draft_pending_pid === null ||
			!session ||
			session.userId !== (mpLeague.sim_runner ?? mpLeague.commissioner)
		) {
			return;
		}

		// Apply the human's pick for the team on the clock. Validate the pid is a
		// real, still-undrafted player first: a bad pid would throw and, because
		// no new realtime event would fire, wedge the whole draft. On an invalid
		// pick we skip applying and re-publish the same slice, which clears the
		// pending pick so the human can try again.
		const order = await draft.getOrder();
		const dp = order[0];
		const pendingPid = mpLeague.draft_pending_pid;
		if (dp && dp.tid === mpLeague.draft_on_clock) {
			const p = await idb.cache.players.get(pendingPid);
			if (p && p.tid === PLAYER.UNDRAFTED) {
				recordDraftSelection({
					dpid: dp.dpid,
					tid: dp.tid,
					round: dp.round,
					pick: dp.pick,
					originalTid: dp.originalTid,
					pid: pendingPid,
				});
				await draft.selectPlayer(dp, pendingPid);
				await draft.afterPicks(false, conditions);
			} else {
				logEvent({
					type: "error",
					text: "A draft pick was rejected (player unavailable). Please pick again.",
					saveToDb: false,
				});
			}
		}

		// Continue the draft: AI picks up to the next human, or finish. If the
		// pick above was skipped, this re-publishes the same slice (same team on
		// the clock) with the pending pick cleared.
		const result = await simDraftSlice(conditions, leagueId, mpLeague.name);
		if (result === "ended") {
			if (mpLeague.checkpoint === "draft") {
				await finishDraftAndContinue(leagueId, conditions);
			} else {
				// Fantasy draft at league start: no checkpoint to advance
				await finishFantasyDraft(leagueId, mpLeague.name);
			}
		}
	} finally {
		processingDraftPick = false;
	}
};

// A member toggled auto_draft ("sim the rest of my picks") - if the draft is
// paused on THEIR pick right now, the runner makes it and rolls on. Called
// from the league_members realtime handler; no-op for everyone but the runner.
export const continueDraftIfAuto = async (
	leagueId: string,
	retriesLeft = 40,
): Promise<void> => {
	// Keep the safety-net poll running while the draft is live (idempotent)
	ensureDraftReconcile(leagueId);
	// A pick or slice is being processed right now (e.g. the runner is still
	// uploading the pause snapshot). Do NOT drop this resume request: the
	// on-clock human likely just enabled auto-draft, and if the toggle's
	// realtime event lands during this window and we no-op with no retry, the
	// draft stalls on their pick forever even though auto_draft is true. Retry
	// once the lock frees (40 x 250ms = 10s, far longer than any pick/upload).
	if (draftPickProcessingBusy()) {
		if (retriesLeft > 0) {
			setTimeout(() => {
				void continueDraftIfAuto(leagueId, retriesLeft - 1);
			}, 250);
		}
		return;
	}
	processingDraftPick = true;
	processingDraftPickStarted = Date.now();
	try {
		const { league: mpLeague, members } = await getLobbyState(leagueId);
		const session = await getSession();
		if (
			!mpLeague.draft_active ||
			mpLeague.draft_pending_pid !== null ||
			mpLeague.draft_on_clock === null ||
			!session ||
			session.userId !== (mpLeague.sim_runner ?? mpLeague.commissioner)
		) {
			return;
		}
		const onClockMember = members.find(
			(m) => m.tid === mpLeague.draft_on_clock,
		);
		if (!onClockMember?.auto_draft) {
			return;
		}

		const conditions = {};
		const order = await draft.getOrder();
		const dp = order[0];
		if (dp && dp.tid === mpLeague.draft_on_clock) {
			const pid = await bestAvailablePid();
			if (pid !== undefined) {
				await draft.selectPlayer(dp, pid);
				await draft.afterPicks(false, conditions);
			}
		}
		const result = await simDraftSlice(conditions, leagueId, mpLeague.name);
		if (result === "ended") {
			if (mpLeague.checkpoint === "draft") {
				await finishDraftAndContinue(leagueId, conditions);
			} else {
				await finishFantasyDraft(leagueId, mpLeague.name);
			}
		}
	} finally {
		processingDraftPick = false;
	}
};

// Safety net for the live draft. Both processDraftPick (a submitted pick) and
// continueDraftIfAuto (an auto-draft toggle) are driven by realtime events; if
// one is ever missed or dropped (a lost frame, a retry that timed out while a
// long slice held the lock), the draft can wedge with a pending pick or an
// on-clock auto member that never gets processed. While the draft is active
// the runner polls the server state and re-drives whichever is stuck. The
// timer self-stops when the draft ends.
let draftReconcileTimer: ReturnType<typeof setInterval> | undefined;

const reconcileDraft = async (leagueId: string) => {
	if (draftPickProcessingBusy()) {
		return; // busy - the in-flight processing will handle it
	}
	let mpLeague;
	let members;
	let session;
	try {
		({ league: mpLeague, members } = await getLobbyState(leagueId));
		session = await getSession();
	} catch {
		return; // transient fetch error; try again next tick
	}
	const isRunner =
		session?.userId === (mpLeague.sim_runner ?? mpLeague.commissioner);
	if (!mpLeague.draft_active || !isRunner) {
		stopDraftReconcile();
		return;
	}
	if (mpLeague.draft_pending_pid !== null) {
		await processDraftPick(leagueId, {});
		return;
	}

	if (mpLeague.draft_on_clock !== null) {
		const onClock = members.find((m) => m.tid === mpLeague.draft_on_clock);
		if (onClock?.auto_draft) {
			await continueDraftIfAuto(leagueId);
			return;
		}
	}

	// Stale on-the-clock repair. draft_on_clock is a snapshot of who owned the
	// next pick when the last slice was published, but a pick can change hands
	// AFTER that: trading during the annual draft is allowed (v16.1), and
	// commit_trade has no idea a draft is running, so it never updates this
	// column. The new owner is then refused by submit_draft_pick ("not on
	// clock") and the old owner can't pick either, which stalls the draft with
	// nothing to re-trigger it. Compare the pointer against who actually owns
	// the next pick locally and re-publish if they disagree.
	try {
		const order = await draft.getOrder();
		const actualOnClock = order[0]?.tid;
		if (
			actualOnClock !== undefined &&
			actualOnClock !== mpLeague.draft_on_clock
		) {
			// Re-drive through simDraftSlice rather than just republishing the
			// pointer. The traded pick often lands on an AI team, and publishing
			// alone would leave that AI on the clock with nobody to pick for it -
			// trading one stall for another. simDraftSlice auto-picks AI teams up
			// to the next human and then publishes.
			processingDraftPick = true;
			processingDraftPickStarted = Date.now();
			try {
				const result = await simDraftSlice({}, leagueId, mpLeague.name);
				if (result === "ended") {
					if (mpLeague.checkpoint === "draft") {
						await finishDraftAndContinue(leagueId, {});
					} else {
						await finishFantasyDraft(leagueId, mpLeague.name);
					}
				}
			} finally {
				processingDraftPick = false;
			}
		}
	} catch {
		// Not in a draftable state right now; the next tick tries again
	}
};

export const ensureDraftReconcile = (leagueId: string) => {
	if (draftReconcileTimer) {
		return;
	}
	draftReconcileTimer = setInterval(() => {
		void reconcileDraft(leagueId);
	}, 2500);
};

export const stopDraftReconcile = () => {
	if (draftReconcileTimer) {
		clearInterval(draftReconcileTimer);
		draftReconcileTimer = undefined;
	}
};

// Runner-only: kick off the live fantasy draft the league opens with. The
// initial snapshot everyone imported is at PHASE.FANTASY_DRAFT with the full
// (snake) order pending; this auto-picks AI teams up to the first human and
// publishes the first slice.
export const mpStartFantasyDraft = async (leagueId: string) => {
	const { league: mpLeague } = await getLobbyState(leagueId);
	const session = await getSession();
	if (
		!session ||
		session.userId !== (mpLeague.sim_runner ?? mpLeague.commissioner)
	) {
		throw new Error("Only the sim runner can start the fantasy draft");
	}
	if (mpLeague.draft_active) {
		return { status: "already_active" as const };
	}
	if (g.get("phase") !== PHASE.FANTASY_DRAFT) {
		logEvent({
			type: "error",
			text: `Cannot start the fantasy draft: league phase is ${g.get("phase")}, expected ${PHASE.FANTASY_DRAFT}.`,
			saveToDb: false,
			persistent: true,
		});
		return { status: "not_fantasy_draft" as const };
	}
	logEvent({
		type: "info",
		text: "Fantasy draft started - picking...",
		saveToDb: false,
		showNotification: true,
	});
	const conditions = {};
	try {
		const result = await simDraftSlice(conditions, leagueId, mpLeague.name);
		if (result === "ended") {
			await finishFantasyDraft(leagueId, mpLeague.name);
		}
	} catch (error) {
		logEvent({
			type: "error",
			text: `Starting the fantasy draft failed: ${error.message}`,
			saveToDb: false,
			persistent: true,
		});
		throw error;
	}
	return { status: "started" as const };
};

// The fantasy draft is over (runPicks' own afterPicks(true) already restored
// leftover players to free agents and returned the phase to PRESEASON).
// Publish the final state and clear the live-draft flags - the checkpoint
// cycle hasn't started yet, so nothing advances.
const finishFantasyDraft = async (leagueId: string, leagueName: string) => {
	await idb.cache.flush();
	const blob = await exportSnapshotBlob(leagueName);
	const path = `${leagueId}/draft-final-${Date.now()}.json.gz`;
	const supabase = getClient();
	const upload = await supabase.storage
		.from("snapshots")
		.upload(path, blob, { upsert: true, contentType: "application/gzip" });
	if (upload.error) {
		throw new Error(
			`Final draft snapshot upload failed: ${upload.error.message}`,
		);
	}
	const { data, error } = await supabase.rpc("finish_draft", {
		p_league_id: leagueId,
		p_snapshot_path: path,
	});
	if (error) {
		throw new Error(error.message);
	}
	await markSeqApplied(leagueId, Number(data.seq));

	// Rosters just went from a shared pool to real teams: rebuild the
	// trade-validation ledger from the drafted state
	try {
		const assets = await collectAssets();
		const reseed = await supabase.rpc("reseed_assets", {
			p_league_id: leagueId,
			p_assets: assets,
		});
		if (reseed.error) {
			throw new Error(reseed.error.message);
		}
	} catch (error) {
		logEvent({
			type: "error",
			text: `Rebuilding the trade ledger failed: ${error.message}. Trades may report false conflicts until the next checkpoint.`,
			saveToDb: false,
		});
	}

	logEvent({
		type: "info",
		text: "Fantasy draft complete! Rosters are set - the season begins at the Preseason checkpoint on the Multiplayer page.",
		saveToDb: false,
		showNotification: true,
	});
	void cleanupOldSnapshots(leagueId, path);
	await toUI("realtimeUpdate", [["playerMovement", "multiplayer"]]);
};

// The human on the clock submits their pick. Returns undefined for non-
// multiplayer / non-draft leagues so the caller falls through to normal
// draftUser.
export const mpDraftPick = async (
	pid: number,
): Promise<{ status: string } | undefined> => {
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		return undefined;
	}
	const leagueId = await getLeagueIdForLid(lid);
	if (!leagueId) {
		return undefined; // single player draft
	}
	const { data, error } = await getClient().rpc("submit_draft_pick", {
		p_league_id: leagueId,
		p_pid: pid,
	});
	if (error) {
		throw new Error(error.message);
	}
	if (data.status === "submitted") {
		const session = await getSession();
		const { league: mpLeague } = await getLobbyState(leagueId);
		if (session?.userId === (mpLeague.sim_runner ?? mpLeague.commissioner)) {
			// The runner applies the pick itself, immediately, rather than waiting
			// for its own realtime echo. Do NOT also apply it optimistically below
			// or the pick would be made twice.
			void processDraftPick(leagueId, {});
		} else {
			await applyOwnPickOptimistically(pid);
		}
	}
	return data;
};

// Show the pick on the picker's own screen straight away instead of leaving
// them staring at an unchanged board until the runner's snapshot lands. This
// is exactly what single-player BBGM does on a pick (see draftUser), and it is
// safe here because a non-runner never publishes anything: the runner's
// snapshot remains authoritative and simply overwrites this when it arrives.
// If the runner rejects the pick, that same snapshot puts the player back in
// the pool and the team back on the clock, so the revert is automatic.
const applyOwnPickOptimistically = async (pid: number) => {
	try {
		const order = await draft.getOrder();
		const dp = order[0];
		// Only ever apply our OWN next pick
		if (!dp || dp.tid !== g.get("userTid")) {
			return;
		}
		const p = await idb.cache.players.get(pid);
		if (!p || p.tid !== PLAYER.UNDRAFTED) {
			return;
		}
		await draft.selectPlayer(dp, pid);
		// false: the runner decides when the draft is actually over
		await draft.afterPicks(false, {});
	} catch (error) {
		// Never let a display shortcut break the real pick, which the server has
		// already accepted at this point
		console.error("Optimistic draft pick display failed:", error);
	}
};

// The draft is complete: advance the sim toward the re-sign checkpoint and
// publish it normally (which clears the draft sub-mode).
const finishDraftAndContinue = async (
	leagueId: string,
	conditions: Conditions,
) => {
	// The end-of-draft snapshot below is authoritative, so any selections not
	// yet broadcast are already covered. Drop them so they can't leak into next
	// season's draft.
	pendingSelections = [];
	const { league: mpLeague } = await getLobbyState(leagueId);
	await beginSegment(leagueId, "resign", mpLeague.name);
	if (g.get("phase") === PHASE.AFTER_DRAFT) {
		if (g.get("spectator")) {
			await league.setGameAttributes({ spectator: false });
		}
		await phase.newPhase(PHASE.RESIGN_PLAYERS, conditions);
	}
	await publishSegment(leagueId);
};

// ============================================================================
// Play menu gating
// ============================================================================

// Options that cannot overshoot the given target (multi-day sims are also
// halted by the after-day hook, so this list mainly blocks phase jumps)
const ALLOWED_OPTIONS: Record<MpCheckpoint, Set<string>> = {
	game25: new Set([
		"day",
		"week",
		"month",
		"untilAllStarGame",
		"untilRegularSeason",
	]),
	deadline: new Set([
		"day",
		"week",
		"month",
		"untilAllStarGame",
		"untilTradeDeadline",
	]),
	playoffs: new Set([
		"day",
		"week",
		"month",
		"untilAllStarGame",
		"untilPlayoffs",
	]),
	// playoffs -> lottery: the runner plays out every playoff game, then stops
	// at the draft lottery. Same play-menu options as simming the playoffs.
	lottery: new Set([
		"day",
		"week",
		"month",
		"untilAllStarGame",
		"untilPlayoffs",
		"untilEndOfRound",
		"untilEndOfPlayIn",
		"throughPlayoffs",
	]),
	draft: new Set([
		"day",
		"week",
		"month",
		"untilAllStarGame",
		"untilPlayoffs",
		"untilEndOfRound",
		"untilEndOfPlayIn",
		"throughPlayoffs",
		"untilDraft",
	]),
	resign: new Set([
		"onePick",
		"untilYourNextPick",
		"untilEnd",
		"untilResignPlayers",
	]),
	fa: new Set(["untilFreeAgency"]),
	offseason: new Set(["day", "week", "month", "untilPreseason"]),
};

// Permissive fallback used only if a checkpoint somehow has no ALLOWED_OPTIONS
// entry - the per-day boundary check still stops the sim at the right place, so
// this just prevents a hard error from wedging the Play menu.
const DEFAULT_SIM_OPTIONS = new Set([
	"day",
	"week",
	"month",
	"untilAllStarGame",
	"untilPlayoffs",
	"untilEndOfRound",
	"untilEndOfPlayIn",
	"throughPlayoffs",
	"untilDraft",
	"untilResignPlayers",
	"untilFreeAgency",
	"untilPreseason",
	"untilRegularSeason",
	"untilTradeDeadline",
]);

const ALWAYS_ALLOWED = new Set(["stop", "stopAuto"]);

type SimContext = {
	leagueId: string;
	target: MpCheckpoint;
	leagueName: string;
};

let currentSegment: SimContext | undefined;

// Fetch fresh server state and decide whether this client may run this play
// menu option right now. Returns an error message, or undefined if allowed.
export const gatePlayMenuOption = async (
	key: string,
): Promise<string | undefined> => {
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		return undefined;
	}
	const leagueId = await getLeagueIdForLid(lid);
	if (!leagueId) {
		return undefined; // single player league - no gating
	}
	if (ALWAYS_ALLOWED.has(key)) {
		return undefined;
	}

	const { league: mpLeague } = await getLobbyState(leagueId);
	const session = await getSession();
	const runnerId = mpLeague.sim_runner ?? mpLeague.commissioner;
	if (!session || session.userId !== runnerId) {
		return "Only the sim runner can advance the season. Make your moves and ready up on the Multiplayer page!";
	}
	if (!mpLeague.sim_open) {
		return "Simulation unlocks when every player has readied up on the Multiplayer page.";
	}

	// During the live draft, picks drive the sim - the runner shouldn't advance
	// the season manually. Route them to wait for picks on the Draft page.
	if (mpLeague.draft_active) {
		return "The draft is in progress - it advances automatically as each team makes its pick on the Draft page.";
	}

	// v16: a scheduled expansion draft interrupted the season. If the pause
	// hasn't been published yet (the phase flipped mid-sim), publish it now;
	// either way the play menu stays blocked until everyone protects + readies
	// and the runner continues via the Multiplayer page button.
	if (g.get("phase") === PHASE.EXPANSION_DRAFT) {
		if (!mpLeague.expansion_active && mpLeague.sim_open) {
			void publishExpansionPause(leagueId, mpLeague.name).catch((error) => {
				logEvent({
					type: "error",
					text: `Publishing the expansion draft pause failed: ${error.message}`,
					saveToDb: false,
				});
			});
			return "<b>Expansion draft!</b> Publishing the pause - everyone protects their players, then readies up to continue.";
		}
		return "Expansion draft in progress - protect your players on the Protect Players page, then everyone readies up and the sim continues from the Multiplayer page.";
	}

	const target = nextCheckpoint(mpLeague.checkpoint);
	if (await boundaryReached(target)) {
		void publishSegment(leagueId);
		return `The ${CHECKPOINT_LABELS[target]} checkpoint has been reached - publishing results now.`;
	}
	// Defensive: a missing ALLOWED_OPTIONS entry must not throw (which would
	// silently break the whole Play menu). Fall back to the common sim options.
	const allowed = ALLOWED_OPTIONS[target] ?? DEFAULT_SIM_OPTIONS;
	if (!allowed.has(key)) {
		return `That would simulate past the ${CHECKPOINT_LABELS[target]} checkpoint. Use day/week/month - the sim stops automatically at the checkpoint.`;
	}

	// First sim action of this segment (Play menu path): pull everyone's latest
	// synced events - especially the lineup/finance events from ready-up - so the
	// runner simulates with them. The "Sim to next checkpoint" button syncs too;
	// the Play menu previously did not, which silently dropped non-host changes.
	if (currentSegment?.leagueId !== leagueId) {
		await syncNow(leagueId);
	}
	await beginSegment(leagueId, target, mpLeague.name);
	return undefined;
};

// First sim action of a segment: remember what we're simulating toward and
// turn on spectator mode so the engine manages every roster (including human
// teams) during the sim - otherwise rosters go illegal in free agency and the
// sim blocks. Spectator is turned back off before the snapshot is exported.
const beginSegment = async (
	leagueId: string,
	target: MpCheckpoint,
	leagueName: string,
) => {
	// Human teams are managed by their humans, so the AI must never sign free
	// agents onto them OR trade their players during the runner's sim - even
	// though spectator mode is on so the engine keeps AI rosters (and, minimally,
	// short human rosters) legal. See autoSign.ts and betweenAiTeams.ts. (local
	// resets on league switch, so this never leaks into a single-player league on
	// the same worker.)
	local.mpProtectUserTeams = true;

	if (currentSegment?.leagueId === leagueId) {
		return;
	}
	currentSegment = { leagueId, target, leagueName };
	mpResetLiveScores();
	await kvSet(dirtyKey(leagueId), target);

	// Spectator mode makes the engine manage every roster (needed so human
	// teams stay legal during game days and the post-auction offseason) -
	// EXCEPT on the way into free agency, where AI re-signing human teams'
	// expiring players would keep them out of the auction pool, and EXCEPT the
	// playoffs (target 'lottery', which only ever starts from the playoffs
	// checkpoint and simulates every playoff game before stopping at the draft
	// lottery): humans manage their own lineups / playing time / injuries
	// through the playoffs like vanilla BBGM, using the lineups everyone locked
	// in at ready-up. No roster churn happens in the playoffs, so the legality
	// concern doesn't apply. (target 'draft' is the lottery->draft segment: no
	// games, so spectator just stays off.)
	if (
		target !== "fa" &&
		target !== "draft" &&
		target !== "lottery" &&
		!g.get("spectator")
	) {
		await league.setGameAttributes({ spectator: true });
	}
	if ((target === "draft" || target === "lottery") && g.get("spectator")) {
		await league.setGameAttributes({ spectator: false });
	}

	// Raise the tradable-draft-pick horizon in leagues created before it was
	// bumped (new leagues already start at the target - see startLeague). One-
	// time: genPicks only ADDS the extra future seasons and preserves already-
	// traded picks (matched by originalTid). The new picks land in this segment's
	// checkpoint snapshot, and the post-publish ledger reseed makes them
	// tradable. Skip during the draft itself so picks aren't disturbed mid-draft;
	// every other checkpoint is safe, so an existing league picks this up on its
	// next sim.
	const inDraftPhase =
		g.get("phase") === PHASE.DRAFT_LOTTERY || g.get("phase") === PHASE.DRAFT;
	if (
		!inDraftPhase &&
		g.get("numSeasonsFutureDraftPicks") < MP_FUTURE_DRAFT_PICK_SEASONS
	) {
		try {
			await league.setGameAttributes({
				numSeasonsFutureDraftPicks: MP_FUTURE_DRAFT_PICK_SEASONS,
			});
			await draft.genPicks({});
		} catch (error) {
			// Never block the sim over this - the attribute is set, so the next
			// natural genPicks (a season change) fills in any missing pick seasons
			logEvent({
				type: "error",
				text: `Extending the draft-pick horizon failed: ${(error as Error).message}`,
				saveToDb: false,
			});
		}
	}
};

// Per-day hook (inside core/game/play.ts's recursion): ONLY halt the day loop
// at the checkpoint. Publishing must not happen here - firing it mid-recursion
// races with game.play's own unwinding and can leave the gameSim lock stuck,
// which would deadlock the next segment. Publishing happens from
// mpPublishIfBoundaryReached after game.play has fully returned.
// True only on the runner while it is actively simulating a checkpoint
// segment. Lets core sim code apply multiplayer-only behavior (e.g. keep
// decaying free-agent demands during the playoffs, since MP players can sign
// free agents then) without affecting single-player leagues.
export const mpInSegment = () => !!currentSegment;

export const mpAfterSimDay = async () => {
	if (!currentSegment) {
		return;
	}
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		return;
	}
	const leagueId = await getLeagueIdForLid(lid);
	if (leagueId !== currentSegment.leagueId) {
		return;
	}
	if (await boundaryReached(currentSegment.target)) {
		await lock.set("stopGameSim", true);
	}
};

// ============================================================================
// Phase 8a: live scores. The runner broadcasts each simulated day's final
// scores over the realtime channel so everyone can watch the sim progress
// (regular season and playoffs). Ephemeral and non-authoritative - the
// checkpoint snapshot remains the source of truth.
// ============================================================================

let liveScoresTotal = 0;
let liveScoresLastSent = 0;

export const mpResetLiveScores = () => {
	liveScoresTotal = 0;
	liveScoresLastSent = 0;
};

// Called from core/game/play.ts after each simulated day. Only the runner
// mid-segment (currentSegment set) broadcasts. Throttled because the sim
// blasts through many days per second - a few updates per second keeps the
// ticker lively without flooding the realtime channel (skipped days are fine,
// they're purely cosmetic).
export const mpBroadcastLiveDay = (
	results: {
		gid: number;
		day: number;
		team: [
			{ id: number; stat: { pts: number } },
			{ id: number; stat: { pts: number } },
		];
	}[],
) => {
	if (!currentSegment || results.length === 0) {
		return;
	}
	liveScoresTotal += results.length;

	const now = Date.now();
	if (now - liveScoresLastSent < 300) {
		return;
	}
	liveScoresLastSent = now;

	const teamInfo = g.get("teamInfoCache");
	const playoffs = g.get("phase") === PHASE.PLAYOFFS;
	const teamCell = (tid: number, pts: number) => ({
		tid,
		abbrev: teamInfo[tid]?.abbrev ?? "???",
		region: teamInfo[tid]?.region ?? "",
		pts,
	});

	const payload: MpLiveScores = {
		season: g.get("season"),
		phase: g.get("phase"),
		day: results[0]?.day ?? 0,
		totalSimmed: liveScoresTotal,
		games: results.map((r) => ({
			gid: r.gid,
			playoffs,
			teams: [
				teamCell(r.team[0].id, r.team[0].stat.pts),
				teamCell(r.team[1].id, r.team[1].stat.pts),
			],
		})),
	};

	// Others get it over the channel; the runner (which doesn't receive its own
	// broadcasts) gets it straight to its own UI so it sees the same ticker.
	broadcastToChannel("live_day", payload);
	void toUI("updateLocal", [{ mpLiveScores: payload }]);
};

// The segment finished - tell everyone to clear the live ticker (the snapshot
// import that follows shows the final, authoritative results).
export const mpBroadcastLiveDone = () => {
	broadcastToChannel("live_done", {});
	void toUI("updateLocal", [{ mpLiveScores: undefined }]);
};

// ============================================================================
// Phase 8b: live-game play-by-play. The runner watches a playoff (or any)
// game in the normal LiveGame; each revealed play is broadcast so spectators
// follow along in a lightweight text feed. Called from the LiveGame UI via the
// mpBroadcastLivePlay / mpEndLiveGame worker APIs. Plays are buffered and
// flushed a few times a second so fast playback (sim a quarter) doesn't flood
// the channel.
// ============================================================================

type LivePlay = { text: string; time: string; score: [number, number] };
let livePlayBuffer: LivePlay[] = [];
let livePlayFlushScheduled = false;
let liveGameInfo:
	| {
			gid: number;
			teams: MpLiveGame["teams"];
			series?: MpLiveGame["series"];
	  }
	| undefined;

// The playoff series standing for a matchup, oriented to (tid0, tid1) so the
// spectator feed can show "their record in the series". Wins reflect games
// completed before this one (playoffSeries updates when a game finishes).
const getSeriesStanding = async (
	tid0: number,
	tid1: number,
): Promise<MpLiveGame["series"]> => {
	const ps = await idb.cache.playoffSeries.get(g.get("season"));
	if (!ps) {
		return undefined;
	}
	const numGamesPlayoffSeries = g.get("numGamesPlayoffSeries", "current");
	for (const [roundIdx, round] of ps.series.entries()) {
		for (const matchup of round) {
			if (!matchup || !matchup.away) {
				continue;
			}
			const { home, away } = matchup;
			const target = helpers.numGamesToWinSeries(
				numGamesPlayoffSeries[roundIdx],
			);
			if (home.tid === tid0 && away.tid === tid1) {
				return { won: [home.won, away.won], target };
			}
			if (home.tid === tid1 && away.tid === tid0) {
				return { won: [away.won, home.won], target };
			}
		}
	}
	return undefined;
};

const flushLivePlays = () => {
	livePlayFlushScheduled = false;
	if (livePlayBuffer.length === 0 || !liveGameInfo) {
		return;
	}
	broadcastToChannel("live_play", {
		gid: liveGameInfo.gid,
		teams: liveGameInfo.teams,
		series: liveGameInfo.series,
		plays: livePlayBuffer,
	});
	livePlayBuffer = [];
};

export const mpBroadcastLivePlay = async (arg: {
	gid: number;
	teams: MpLiveGame["teams"];
	play: LivePlay;
}) => {
	// New game: look up the series standing once (per-play lookups would be
	// wasteful, and the standing doesn't change mid-game)
	if (!liveGameInfo || liveGameInfo.gid !== arg.gid) {
		const series = await getSeriesStanding(arg.teams[0].tid, arg.teams[1].tid);
		liveGameInfo = { gid: arg.gid, teams: arg.teams, series };
	}
	livePlayBuffer.push(arg.play);
	if (!livePlayFlushScheduled) {
		livePlayFlushScheduled = true;
		setTimeout(flushLivePlays, 250);
	}
};

// The runner left the live game (or it ended) - flush the tail and tell
// spectators the feed is done.
export const mpEndLiveGame = () => {
	flushLivePlays();
	if (liveGameInfo) {
		broadcastToChannel("live_game_end", { gid: liveGameInfo.gid });
	}
	liveGameInfo = undefined;
	livePlayBuffer = [];
};

// Called after a sim fully completes (from the play-menu wrapper and the
// simToNextCheckpoint loop), once gameSim is guaranteed clear. Publishes the
// segment if the checkpoint boundary has been reached.
export const mpPublishIfBoundaryReached = async () => {
	if (!currentSegment) {
		return;
	}
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		return;
	}
	const leagueId = await getLeagueIdForLid(lid);
	if (leagueId !== currentSegment.leagueId) {
		return;
	}
	if (await boundaryReached(currentSegment.target)) {
		await publishSegment(leagueId);
	}
};

// ============================================================================
// Publishing a finished segment
// ============================================================================

let publishPromise: Promise<void> | undefined;

// Idempotent: concurrent triggers (per-day hook + sim loop) share one publish
export const publishSegment = (leagueId: string): Promise<void> => {
	if (!publishPromise) {
		publishPromise = doPublish(leagueId).finally(() => {
			publishPromise = undefined;
		});
	}
	return publishPromise;
};

const doPublish = async (leagueId: string) => {
	try {
		// Never publish a half-finished segment: the boundary must be reached
		const target = (await kvGet(dirtyKey(leagueId))) as MpCheckpoint | null;
		if (target && !(await boundaryReached(target))) {
			logEvent({
				type: "error",
				text: `Cannot publish yet: the ${CHECKPOINT_LABELS[target]} checkpoint has not been reached. Keep simulating.`,
				saveToDb: false,
			});
			return;
		}

		// Wait for any in-flight game sim to fully settle before snapshotting,
		// so the export reflects a consistent, finished state
		for (let j = 0; j < 120 && lock.get("gameSim"); j++) {
			await new Promise((resolve) => {
				setTimeout(resolve, 250);
			});
		}

		await updateStatus("Publishing...");
		const segment = currentSegment;

		// The canonical snapshot must not have spectator mode baked in
		if (g.get("spectator")) {
			await league.setGameAttributes({ spectator: false });
		}
		await idb.cache.flush();

		const name = segment?.leagueName ?? "League";
		const blob = await exportSnapshotBlob(name);
		const path = `${leagueId}/checkpoint-${Date.now()}.json.gz`;
		const supabase = getClient();

		const upload = await supabase.storage.from("snapshots").upload(path, blob, {
			upsert: true,
			contentType: "application/gzip",
		});
		if (upload.error) {
			throw new Error(`Snapshot upload failed: ${upload.error.message}`);
		}

		const { data, error } = await supabase.rpc("complete_sim", {
			p_league_id: leagueId,
			p_snapshot_path: path,
		});
		if (error) {
			throw new Error(error.message);
		}

		if (data.status === "published") {
			// We ARE the canonical state; skip applying our own sim event
			await markSeqApplied(leagueId, Number(data.seq));
			await kvRemove(dirtyKey(leagueId));
			currentSegment = undefined;

			// Clear everyone's live-score ticker; the snapshot import that the
			// 'sim' event triggers shows the final, authoritative results.
			mpBroadcastLiveDone();

			// The sim changed ownership everywhere (draft picks used, players
			// drafted/signed/retired), so rebuild the trade-validation ledger
			// from the canonical state
			try {
				const assets = await collectAssets();
				const reseed = await supabase.rpc("reseed_assets", {
					p_league_id: leagueId,
					p_assets: assets,
				});
				if (reseed.error) {
					throw new Error(reseed.error.message);
				}
			} catch (error) {
				logEvent({
					type: "error",
					text: `Rebuilding the trade ledger failed: ${error.message}. Trades may report false conflicts until the next checkpoint.`,
					saveToDb: false,
				});
			}

			// Entering the playoffs checkpoint: record which human teams made the
			// playoffs, so only they gate the ready-up (everyone else just watches)
			if (data.to === "playoffs") {
				try {
					const alive = await computeAliveHumanTids();
					const res = await supabase.rpc("set_playoff_alive", {
						p_league_id: leagueId,
						p_alive: alive,
					});
					if (res.error) {
						throw new Error(res.error.message);
					}
				} catch (error) {
					logEvent({
						type: "error",
						text: `Marking playoff teams failed: ${error.message}. All players will need to ready up instead.`,
						saveToDb: false,
					});
				}
			}

			// Entering the re-sign checkpoint: publish the re-sign candidates
			// (the expiring players each human may re-sign) from the negotiations
			// newPhaseResignPlayers just created.
			if (data.to === "resign") {
				try {
					await uploadResignCandidates(leagueId);
				} catch (error) {
					logEvent({
						type: "error",
						text: `Publishing re-sign candidates failed: ${error.message}`,
						saveToDb: false,
					});
				}
			}

			// Phase 7: at the season-end offseason, evaluate firing conditions
			// and stage them so the commissioner can override before we advance.
			if (data.to === "offseason") {
				try {
					await stageFirings(leagueId);
				} catch (error) {
					logEvent({
						type: "error",
						text: `Evaluating commissioner firings failed: ${error.message}`,
						saveToDb: false,
					});
				}
			}

			logEvent({
				type: "info",
				text:
					data.to === "playoffs"
						? "Checkpoint published! <b>The playoffs are set</b> - playoff teams, ready up to tip them off."
						: data.to === "resign"
							? "Checkpoint published! <b>Re-sign your players</b> on the Re-sign Players page before free agency opens."
							: data.to === "fa"
								? "Checkpoint published! <b>Free agency is open</b> - submit your secret bids on the Free Agents page. Trades are open this checkpoint too (before players develop for next season)."
								: `Checkpoint published! The league is now at <b>${CHECKPOINT_LABELS[data.to as MpCheckpoint] ?? data.to}</b>. Everyone's trade budgets have reset.`,
				saveToDb: false,
				showNotification: true,
			});

			void cleanupOldSnapshots(leagueId, path);
		} else if (data.status === "sim_not_open") {
			// Already published (double trigger) - just resync
			await kvRemove(dirtyKey(leagueId));
			currentSegment = undefined;
			await syncNow(leagueId);
		}

		await toUI("realtimeUpdate", [["multiplayer"]]);
	} catch (error) {
		logEvent({
			type: "error",
			text: `Publishing the checkpoint failed: ${error.message}. Use "Publish results" on the Multiplayer page to retry.`,
			saveToDb: false,
			persistent: true,
		});
	} finally {
		await updateStatus("Idle");
	}
};

// After simming into the re-sign checkpoint, newPhaseResignPlayers has made
// every human team's expiring players free agents with a re-sign negotiation.
// Publish those as the re-sign candidates so each human can re-sign their own.
const uploadResignCandidates = async (leagueId: string) => {
	const negotiations = await idb.cache.negotiations.getAll();
	const candidates: {
		pid: number;
		tid: number;
		amount: number;
		ovr: number;
		name: string;
		exp: number;
		willing: boolean;
	}[] = [];
	for (const negotiation of negotiations) {
		if (!negotiation.resigning) {
			continue;
		}
		const p = await idb.cache.players.get(negotiation.pid);
		if (!p) {
			continue;
		}
		// Mood-adjusted, per-team demand - exactly like vanilla's re-sign page,
		// which shows p.mood.user.contractAmount. An unhappy player demands more
		// (up to the max contract) to re-sign, so mood actually matters instead
		// of everyone re-signing at their raw asking price.
		//
		// `willing` is vanilla's mood-based refusal (moodInfo's seeded roll
		// against probWilling). Vanilla enforces it in contractNegotiation's
		// accept() - "Player is no longer willing to negotiate" - and the
		// Re-sign page shows "Refuses!" instead of a Negotiate button. The
		// runner computes it once here so every client agrees on who refused.
		const mood = await player.moodInfo(p, negotiation.tid);
		candidates.push({
			pid: p.pid,
			tid: negotiation.tid,
			amount: mood.contractAmount,
			ovr: p.ratings.at(-1)!.ovr,
			name: `${p.firstName} ${p.lastName}`,
			exp: p.contract.exp,
			willing: mood.willing,
		});
	}

	const { error } = await getClient().rpc("set_resign_candidates", {
		p_league_id: leagueId,
		p_candidates: candidates,
	});
	if (error) {
		throw new Error(error.message);
	}
};

// Keep the two newest checkpoint snapshots plus the original league file
const cleanupOldSnapshots = async (leagueId: string, latestPath: string) => {
	try {
		const supabase = getClient();
		const { data } = await supabase.storage.from("snapshots").list(leagueId);
		const checkpoints = (data ?? [])
			.filter((f) => f.name.startsWith("checkpoint-"))
			.sort((a, b) => b.name.localeCompare(a.name));
		const doomed = checkpoints
			.slice(2)
			.map((f) => `${leagueId}/${f.name}`)
			.filter((p) => p !== latestPath);
		if (doomed.length > 0) {
			await supabase.storage.from("snapshots").remove(doomed);
		}
	} catch {
		// Cleanup is best-effort
	}
};

// ============================================================================
// One-click "Sim to next checkpoint"
// ============================================================================

export const simToNextCheckpoint = async (conditions: Conditions) => {
	const lid = g.get("lid");
	if (typeof lid !== "number") {
		throw new Error("Not in a league");
	}
	const leagueId = await getLeagueIdForLid(lid);
	if (!leagueId) {
		throw new Error("Not a multiplayer league");
	}

	const { league: mpLeague } = await getLobbyState(leagueId);
	const session = await getSession();
	if (
		!session ||
		session.userId !== (mpLeague.sim_runner ?? mpLeague.commissioner)
	) {
		throw new Error("Only the sim runner can simulate");
	}
	if (!mpLeague.sim_open) {
		throw new Error("Everyone must ready up before simulating");
	}

	// Phase 6b: the live draft advances itself as humans pick (via
	// processDraftPick on realtime pick events). Manually simming would
	// republish the same slice, so no-op here.
	if (mpLeague.draft_active) {
		return { status: "draft_paused" as const };
	}

	// Apply any not-yet-applied events (especially everyone's lineup events
	// from their ready-ups) so the sim uses the latest synced state
	await syncNow(leagueId);

	const target = nextCheckpoint(mpLeague.checkpoint);
	await beginSegment(leagueId, target, mpLeague.name);

	// Phase 7: leaving the offseason, apply any commissioner firings that
	// survived override, then sync them into our own league before simming.
	if (mpLeague.checkpoint === "offseason") {
		try {
			const { applied } = await applyPendingFirings(leagueId);
			if (applied > 0) {
				await syncNow(leagueId);
			}
		} catch (error) {
			logEvent({
				type: "error",
				text: `Applying commissioner firings failed: ${error.message}`,
				saveToDb: false,
			});
		}
	}

	// Defensive: a wedged gameSim lock from a prior sim would make game.play
	// refuse to start. If nothing is actually simming, clear it.
	if (lock.get("gameSim")) {
		await lock.set("gameSim", false);
	}

	// Walk the season forward, phase by phase, until the checkpoint. Multi-day
	// sims are also halted by the after-day hook the moment the boundary hits.
	for (let i = 0; i < 500; i++) {
		if (await boundaryReached(target)) {
			break;
		}
		const currentPhase = g.get("phase");

		if (currentPhase === PHASE.PRESEASON) {
			await phase.newPhase(PHASE.REGULAR_SEASON, conditions);
		} else if (
			currentPhase === PHASE.REGULAR_SEASON ||
			currentPhase === PHASE.AFTER_TRADE_DEADLINE ||
			// v10: playoff games sim like regular-season days; the sim halted at the
			// 'playoffs' checkpoint for one ready-up, and from there the runner
			// controls the pace freely to the draft
			currentPhase === PHASE.PLAYOFFS
		) {
			await lock.set("stopGameSim", false);
			await game.play(7, conditions);
			// game.play's top-level promise resolves before its recursive day
			// chain finishes, so wait for the sim lock to clear before checking
			// the boundary. If it never clears (wedged), force it so we retry
			// rather than sleeping forever.
			let cleared = false;
			for (let j = 0; j < 600; j++) {
				if (!lock.get("gameSim")) {
					cleared = true;
					break;
				}
				await new Promise((resolve) => {
					setTimeout(resolve, 500);
				});
			}
			if (!cleared) {
				await lock.set("gameSim", false);
			}
		} else if (currentPhase === PHASE.DRAFT_LOTTERY) {
			await phase.newPhase(PHASE.DRAFT, conditions);
		} else if (currentPhase === PHASE.EXPANSION_DRAFT) {
			// v16: a scheduled expansion (new team joining) fired during this
			// segment and moved the phase to EXPANSION_DRAFT.
			const ed = g.get("expansionDraft");
			if (ed.phase === "protection") {
				if (!mpLeague.expansion_active) {
					// First encounter: pause + publish so every human protects their
					// own players, then re-readies. The segment resumes from here.
					await publishExpansionPause(leagueId, mpLeague.name);
					return { status: "expansion_paused" as const };
				}
				// Resumed (everyone protected + readied): lock in protection lists
				// (humans' synced choices or auto-protect; see start.ts) and move to
				// the expansion draft itself
				await expansionDraftCore.start();
			} else if (ed.phase === "draft") {
				// Expansion teams are AI, so the whole expansion draft runs in one
				// go; afterPicks(true) inside runPicks restores the interrupted phase
				await draft.runPicks({ type: "untilEnd" }, conditions);

				// The league just gained teams, so draft classes are now short of the
				// required size (round(rounds * teams * 7/6) per class). Importers'
				// createStream tops up EVERY future class automatically with RANDOM
				// fillers (addDraftProspects iterates all prospect years) - so the
				// runner must generate them FIRST, making its snapshot canonical;
				// otherwise every client rolls its own fillers and the leagues
				// diverge. genPlayers only generates the shortfall, so importers then
				// find full classes and add nothing. (Real leagues carry generated
				// classes many years out - e.g. a 2030 class in a 1980 league - and
				// ANY class below the new target diverges.)
				const seasonOffset = g.get("phase") >= PHASE.RESIGN_PLAYERS ? 1 : 0;
				const upcomingDraftYear = g.get("season") + seasonOffset;
				const prospects = await idb.cache.players.indexGetAll(
					"playersByTid",
					PLAYER.UNDRAFTED,
				);
				const classYears = new Set<number>([
					upcomingDraftYear,
					upcomingDraftYear + 1,
					upcomingDraftYear + 2,
				]);
				for (const p of prospects) {
					if (
						typeof p.draft.year === "number" &&
						p.draft.year >= upcomingDraftYear
					) {
						classYears.add(p.draft.year);
					}
				}
				for (const year of [...classYears].sort((x, y) => x - y)) {
					if (
						year === upcomingDraftYear &&
						g.get("phase") > PHASE.DRAFT_LOTTERY &&
						g.get("phase") < PHASE.RESIGN_PLAYERS
					) {
						// Between the draft and re-sign, this year's class is already
						// drafted - addDraftProspects skips it too
						continue;
					}
					await draft.genPlayers(year);
				}
			} else {
				throw new Error(
					`Unexpected expansion draft state "${ed.phase}" during the sim`,
				);
			}
		} else if (currentPhase === PHASE.DRAFT) {
			// Phase 6b: live draft - auto-pick AI up to the next human, then pause
			// so that human picks on their own screen.
			const result = await simDraftSlice(conditions, leagueId, mpLeague.name);
			if (result === "paused") {
				return { status: "draft_paused" as const };
			}
			// "ended" -> loop continues to AFTER_DRAFT
		} else if (currentPhase === PHASE.AFTER_DRAFT) {
			// Turn spectator OFF so newPhaseResignPlayers creates re-sign
			// negotiations for human teams (rather than AI-deciding for them),
			// which is what makes the re-sign candidates
			if (g.get("spectator")) {
				await league.setGameAttributes({ spectator: false });
			}
			await phase.newPhase(PHASE.RESIGN_PLAYERS, conditions);
		} else if (currentPhase === PHASE.RESIGN_PLAYERS) {
			await phase.newPhase(PHASE.FREE_AGENCY, conditions);
		} else if (currentPhase === PHASE.FREE_AGENCY) {
			await freeAgents.play(g.get("daysLeft"), conditions);
			for (let j = 0; j < 600; j++) {
				if (!lock.get("gameSim")) {
					break;
				}
				await new Promise((resolve) => {
					setTimeout(resolve, 500);
				});
			}
		} else {
			throw new Error(
				`Sim loop does not know how to advance from phase ${currentPhase}`,
			);
		}
	}

	if (await boundaryReached(target)) {
		await publishSegment(leagueId);
		return { status: "published" as const };
	}
	return { status: "stopped" as const };
};
